<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else-if="batchOperation" class="detail-container">
      <!-- 基本資訊 -->
      <div class="detail-section">
        <h2 class="section-title">基本資訊</h2>
        <div class="info-grid">
          <div class="info-item">
            <label>批次 ID</label>
            <code class="code-badge">{{ batchOperation.batchId }}</code>
          </div>
          <div class="info-item">
            <label>操作類型</label>
            <span>{{ getOperationTypeText(batchOperation.operationType) }}</span>
          </div>
          <div class="info-item">
            <label>操作名稱</label>
            <span>{{ batchOperation.operationName }}</span>
          </div>
          <div class="info-item">
            <label>操作原因</label>
            <span>{{ batchOperation.operationReason }}</span>
          </div>
          <div class="info-item">
            <label>狀態</label>
            <span :class="getStatusBadgeClass(batchOperation.status)">
              {{ getStatusText(batchOperation.status) }}
            </span>
          </div>
          <div class="info-item">
            <label>建立時間</label>
            <span>{{ formatDateTime(batchOperation.createdAt) }}</span>
          </div>
          <div class="info-item">
            <label>建立者</label>
            <span>{{ batchOperation.createdByName || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 執行結果 -->
      <div v-if="batchOperation.status === 'completed' || batchOperation.status === 'processing'" class="detail-section">
        <h2 class="section-title">執行結果</h2>
        <div class="info-grid">
          <div class="info-item">
            <label>總數</label>
            <span>{{ batchOperation.totalCount || 0 }}</span>
          </div>
          <div class="info-item">
            <label>成功</label>
            <span class="text-success">{{ batchOperation.successCount || 0 }}</span>
          </div>
          <div class="info-item">
            <label>失敗</label>
            <span class="text-danger">{{ batchOperation.failedCount || 0 }}</span>
          </div>
          <div class="info-item">
            <label>跳過</label>
            <span>{{ batchOperation.skippedCount || 0 }}</span>
          </div>
          <div v-if="batchOperation.status === 'processing'" class="info-item">
            <label>進度</label>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: `${batchOperation.progressPercentage || 0}%` }"></div>
              <span class="progress-text">{{ batchOperation.progressPercentage || 0 }}%</span>
            </div>
          </div>
          <div v-if="batchOperation.executionDuration" class="info-item">
            <label>執行時間</label>
            <span>{{ batchOperation.executionDuration }} 秒</span>
          </div>
        </div>
      </div>

      <!-- 操作按鈕 -->
      <div class="detail-actions">
        <button 
          v-if="batchOperation.status === 'draft' || batchOperation.status === 'pending'"
          class="btn btn-primary" 
          @click="handleExecute"
          :disabled="executing"
        >
          <i class="ri-play-line"></i>
          {{ executing ? '執行中...' : '執行批次操作' }}
        </button>
        <button 
          v-if="batchOperation.status === 'completed' || batchOperation.status === 'failed'"
          class="btn btn-secondary" 
          @click="showDetails = !showDetails"
        >
          <i class="ri-file-list-line"></i>
          {{ showDetails ? '隱藏' : '查看' }}執行明細
        </button>
      </div>

      <!-- 執行明細 -->
      <div v-if="showDetails" class="detail-section">
        <h2 class="section-title">執行明細</h2>
        <div v-if="detailsLoading" class="loading-state">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>
        <div v-else-if="detailsError" class="error-state">
          <p class="error-message">{{ detailsError }}</p>
        </div>
        <div v-else class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>會員姓名</th>
                <th>會員等級</th>
                <th>狀態</th>
                <th>錯誤訊息</th>
                <th>執行時間</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="executionDetails.length === 0">
                <td colspan="5" class="empty-state">沒有執行明細</td>
              </tr>
              <tr v-for="detail in executionDetails" :key="detail.id">
                <td>{{ detail.memberName || '-' }}</td>
                <td>{{ detail.memberTier || '-' }}</td>
                <td>
                  <span :class="getDetailStatusBadgeClass(detail.status)">
                    {{ getDetailStatusText(detail.status) }}
                  </span>
                </td>
                <td>{{ detail.errorMessage || '-' }}</td>
                <td>{{ formatDateTime(detail.executedAt) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- 分頁 -->
          <ListPagination
            v-if="detailsPagination && detailsPagination.totalPages > 1"
            :page="detailsPagination.page"
            :total-pages="detailsPagination.totalPages"
            @change="changeDetailsPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { batchOperationService } from '@/api/modules/batchOperations.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';

export default {
  name: 'BatchOperationDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次操作詳情');
    const batchOperation = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const executing = ref(false);
    const showDetails = ref(false);
    const executionDetails = ref([]);
    const detailsLoading = ref(false);
    const detailsError = ref(null);
    const detailsPagination = ref(null);

    const loadBatchOperation = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await batchOperationService.getBatchOperation(route.params.batchId);

        if (response && response.success && response.data) {
          batchOperation.value = response.data;
        } else {
          error.value = response?.message || '獲取批次操作詳情失敗';
        }
      } catch (err) {
        console.error('獲取批次操作詳情失敗:', err);
        error.value = err.message || '獲取批次操作詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const loadExecutionDetails = async () => {
      if (!showDetails.value) return;

      detailsLoading.value = true;
      detailsError.value = null;

      try {
        const params = {
          page: detailsPagination.value?.page || 1,
          pageSize: 20
        };

        const response = await batchOperationService.getExecutionDetails(route.params.batchId, params);

        if (response && response.success && response.data) {
          executionDetails.value = response.data.items || [];
          detailsPagination.value = response.data.pagination;
        } else {
          detailsError.value = response?.message || '獲取執行明細失敗';
        }
      } catch (err) {
        console.error('獲取執行明細失敗:', err);
        detailsError.value = err.message || '獲取執行明細失敗';
      } finally {
        detailsLoading.value = false;
      }
    };

    const changeDetailsPage = (page) => {
      if (detailsPagination.value) {
        detailsPagination.value.page = page;
      }
      loadExecutionDetails();
    };

    const handleExecute = async () => {
      const confirmed = await showConfirm(
        '確認執行',
        '確定要執行此批次操作嗎？執行後無法取消。'
      );

      if (!confirmed) return;

      executing.value = true;

      try {
        const response = await batchOperationService.executeBatchOperation(route.params.batchId);

        if (response && response.success) {
          await showSuccess('執行成功', '批次操作已開始執行');
          await loadBatchOperation();
        } else {
          await showError('執行失敗', response?.message || '執行批次操作失敗');
        }
      } catch (err) {
        console.error('執行批次操作失敗:', err);
        await showError('執行失敗', err.message || '執行批次操作失敗');
      } finally {
        executing.value = false;
      }
    };

    const handleBack = () => {
      router.push('/client/batch-operations');
    };

    const getOperationTypeText = (type) => {
      const map = {
        'update_members': '更新會員',
        'adjust_points': '調整點數',
        'issue_coupons': '發放優惠券',
        'void_coupons': '作廢優惠券',
        'send_messages': '發送訊息',
        'export_members': '匯出會員'
      };
      return map[type] || type;
    };

    const getStatusText = (status) => {
      const map = {
        'draft': '草稿',
        'pending': '待執行',
        'processing': '執行中',
        'completed': '已完成',
        'failed': '失敗',
        'cancelled': '已取消'
      };
      return map[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const map = {
        'draft': 'badge badge-secondary',
        'pending': 'badge badge-warning',
        'processing': 'badge badge-info',
        'completed': 'badge badge-success',
        'failed': 'badge badge-danger',
        'cancelled': 'badge badge-secondary'
      };
      return map[status] || 'badge badge-secondary';
    };

    const getDetailStatusText = (status) => {
      const map = {
        'success': '成功',
        'failed': '失敗',
        'skipped': '跳過'
      };
      return map[status] || status;
    };

    const getDetailStatusBadgeClass = (status) => {
      const map = {
        'success': 'badge badge-success',
        'failed': 'badge badge-danger',
        'skipped': 'badge badge-secondary'
      };
      return map[status] || 'badge badge-secondary';
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    watch(showDetails, (newVal) => {
      if (newVal) {
        loadExecutionDetails();
      }
    });

    onMounted(() => {
      loadBatchOperation();
    });

    return {
      pageTitle,
      batchOperation,
      loading,
      error,
      executing,
      showDetails,
      executionDetails,
      detailsLoading,
      detailsError,
      detailsPagination,
      loadBatchOperation,
      loadExecutionDetails,
      changeDetailsPage,
      handleExecute,
      handleBack,
      getOperationTypeText,
      getStatusText,
      getStatusBadgeClass,
      getDetailStatusText,
      getDetailStatusBadgeClass,
      formatDateTime
    };
  }
};
</script>

<style scoped>
.detail-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.detail-section {
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item label {
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
}

.info-item span {
  color: #333;
}

.detail-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.progress-bar {
  position: relative;
  width: 200px;
  height: 24px;
  background-color: #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

.progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: bold;
  color: #333;
  z-index: 1;
}

.text-success {
  color: #28a745;
  font-weight: 600;
}

.text-danger {
  color: #dc3545;
  font-weight: 600;
}
</style>
